import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Globe, FileCheck, Server, Eye, Clock, ChevronDown, ChevronRight, Download, ExternalLink, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';
import Header from '../components/layout/Header';
import { Footer, TrustBadges, DebateSandbox } from '../components/marketing';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
};

const COMPLIANCE_DATA = [
  { standard: 'SOC 2 Type II', status: 'in-progress', target: 'Q3 2026', description: 'Service organization controls for security, availability, and confidentiality', icon: Shield },
  { standard: 'GDPR', status: 'active', target: 'Active', description: 'Full compliance with EU General Data Protection Regulation', icon: Globe },
  { standard: 'CCPA', status: 'active', target: 'Active', description: 'California Consumer Privacy Act compliance for US users', icon: FileCheck },
  { standard: 'ISO 27001', status: 'planned', target: 'Q1 2027', description: 'Information security management system certification', icon: Lock },
];

const SUB_PROCESSORS = [
  { name: 'OpenAI', purpose: 'AI model inference (GPT-4, GPT-3.5)', location: 'United States', dpa: true },
  { name: 'Anthropic', purpose: 'AI model inference (Claude)', location: 'United States', dpa: true },
  { name: 'Google AI', purpose: 'AI model inference (Gemini)', location: 'United States', dpa: true },
  { name: 'Supabase', purpose: 'Database and authentication', location: 'United States (AWS)', dpa: true },
  { name: 'Vercel', purpose: 'Frontend hosting and CDN', location: 'Global (edge network)', dpa: true },
  { name: 'Stripe', purpose: 'Payment processing', location: 'United States', dpa: true },
];

const FAQ_ITEMS = [
  {
    q: 'Does Fray train AI models on my data?',
    a: 'No. We explicitly opt out of training with all model providers. Your debates, prompts, and decisions are never used to train any AI model. This is contractually enforced through our DPAs with each provider.',
  },
  {
    q: 'Where is my data stored?',
    a: 'All debate data is stored in Supabase (hosted on AWS us-east-1). File attachments and context documents are encrypted at rest with AES-256. We support data residency requirements for Enterprise customers.',
  },
  {
    q: 'How do you handle data deletion requests?',
    a: 'Users can delete their account and all associated data at any time from Settings. Enterprise admins can purge workspace data. We honor all GDPR/CCPA deletion requests within 30 days. Backup retention is 90 days maximum.',
  },
  {
    q: 'What happens during a security incident?',
    a: 'We follow a documented incident response plan: detection → containment → eradication → recovery → post-mortem. Enterprise customers are notified within 72 hours per GDPR requirements. All incidents are logged and reviewed.',
  },
  {
    q: 'Can I use Fray with my own API keys (BYOK)?',
    a: 'Yes. Bring Your Own Keys mode means your API calls go directly to model providers through your own accounts. Fray never sees or stores your API keys — they are encrypted client-side and used only for the duration of each debate.',
  },
  {
    q: 'Is there a penetration testing report available?',
    a: 'We conduct annual third-party penetration testing. Enterprise customers can request the executive summary under NDA. Contact our security team for access.',
  },
];

export default function SecurityPage() {
  const [dpaForm, setDpaForm] = useState({ email: '', company: '', useCase: '' });
  const [dpaSubmitted, setDpaSubmitted] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 md:pt-36 md:pb-24 md:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[rgba(245,241,232,0.08)] text-[rgba(245,241,232,0.48)] text-xs font-mono mb-6 tracking-wider">
              <Shield className="w-3 h-3 text-[#FF4D00]" />
              TRUST CENTER
            </div>

            <h1 className="text-display-lg md:text-display-xl font-display text-[#F5F1E8] uppercase mb-6">
              Enterprise-Grade Security<br />
              <span className="text-[#FF4D00]">For AI-Powered Decisions</span>
            </h1>

            <p className="text-lg text-[rgba(245,241,232,0.72)] max-w-2xl mb-8 font-body leading-relaxed">
              Your decisions are sensitive. We treat them that way. End-to-end encryption,
              training opt-out guarantees, and compliance frameworks built for enterprise procurement.
            </p>

            <TrustBadges className="mb-8" linkToSecurity={false} />
          </motion.div>
        </div>
      </section>

      {/* Compliance Status Table */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionHeader number="01" title="Compliance Status" />
            <div className="grid gap-3 mt-8">
              {COMPLIANCE_DATA.map((item, i) => (
                <motion.div
                  key={item.standard}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-center gap-4 p-4 bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)]"
                >
                  <item.icon className="w-5 h-5 text-[rgba(245,241,232,0.48)] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-0.5">
                      <span className="text-sm font-mono text-[#F5F1E8] font-medium">{item.standard}</span>
                      <StatusBadge status={item.status} />
                    </div>
                    <p className="text-xs text-[rgba(245,241,232,0.48)]">{item.description}</p>
                  </div>
                  <span className="text-xs font-mono text-[rgba(245,241,232,0.48)] shrink-0">
                    {item.target}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Data Flow Diagram */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionHeader number="02" title="Data Flow Architecture" />
            <div className="mt-8 p-6 md:p-8 bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] relative grain-overlay">
              <div className="relative flex flex-col md:flex-row items-center justify-between gap-6 md:gap-4">
                {[
                  { label: 'Your Team', sublabel: 'Browser / MCP Client', icon: '01' },
                  { label: 'Fray Platform', sublabel: 'Encrypted Transit (TLS 1.3)', icon: '02' },
                  { label: 'Model Providers', sublabel: 'Training Opt-Out Enforced', icon: '03' },
                  { label: 'Decision Output', sublabel: 'AES-256 At Rest', icon: '04' },
                ].map((step, i) => (
                  <React.Fragment key={step.label}>
                    {i > 0 && (
                      <div className="hidden md:block w-12 h-px bg-gradient-to-r from-[#FF4D00]/60 to-[#FF4D00]/20" />
                    )}
                    <div className="flex-1 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 bg-[#0A0A0A] border border-[rgba(245,241,232,0.08)] flex items-center justify-center">
                        <span className="text-xs font-mono text-[#FF4D00]">{step.icon}</span>
                      </div>
                      <p className="text-sm font-mono text-[#F5F1E8] mb-0.5">{step.label}</p>
                      <p className="text-[10px] font-mono text-[rgba(245,241,232,0.48)]">{step.sublabel}</p>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-[rgba(245,241,232,0.08)]">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#F2A900] shrink-0 mt-0.5" />
                  <p className="text-xs text-[rgba(245,241,232,0.72)]">
                    <strong className="text-[#F2A900]">Training opt-out guarantee:</strong> All model providers are contractually prohibited from using your debate data for training.
                    BYOK users' API calls are routed directly — Fray never intercepts or stores model responses.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DPA Download */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionHeader number="03" title="Data Processing Agreement" />
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-[rgba(245,241,232,0.72)] leading-relaxed mb-4 font-body">
                  Download our standard DPA for GDPR compliance. Covers data processing terms,
                  sub-processor management, breach notification, and data subject rights.
                </p>
                <p className="text-sm text-[rgba(245,241,232,0.72)] leading-relaxed font-body">
                  Enterprise customers can request custom DPA amendments. Contact our security
                  team for SIG Lite questionnaire responses and additional compliance documentation.
                </p>
              </div>
              <div className="bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] p-6">
                {dpaSubmitted ? (
                  <div className="text-center py-4">
                    <CheckCircle2 className="w-8 h-8 text-[#22C55E] mx-auto mb-3" />
                    <p className="text-sm text-[#F5F1E8] font-mono">DPA sent to your email</p>
                  </div>
                ) : (
                  <>
                    <h4 className="text-sm font-mono text-[#F5F1E8] mb-4 tracking-wider uppercase">Request DPA</h4>
                    <div className="space-y-3">
                      <input
                        type="email"
                        placeholder="Work email"
                        value={dpaForm.email}
                        onChange={e => setDpaForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-[rgba(245,241,232,0.08)] text-[#F5F1E8] px-3 py-2.5 text-sm font-body placeholder:text-[rgba(245,241,232,0.32)] focus:outline-none focus:border-[#FF4D00] transition-colors"
                      />
                      <input
                        type="text"
                        placeholder="Company name"
                        value={dpaForm.company}
                        onChange={e => setDpaForm(f => ({ ...f, company: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-[rgba(245,241,232,0.08)] text-[#F5F1E8] px-3 py-2.5 text-sm font-body placeholder:text-[rgba(245,241,232,0.32)] focus:outline-none focus:border-[#FF4D00] transition-colors"
                      />
                      <button
                        onClick={() => setDpaSubmitted(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Request DPA
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sub-processor List */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionHeader number="04" title="Sub-processors" />
            <div className="mt-8 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(245,241,232,0.08)]">
                    {['Provider', 'Purpose', 'Location', 'DPA'].map(h => (
                      <th key={h} className="text-left text-[10px] font-mono text-[rgba(245,241,232,0.48)] tracking-wider uppercase py-3 px-4">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SUB_PROCESSORS.map((sp, i) => (
                    <tr key={sp.name} className="border-b border-[rgba(245,241,232,0.04)] hover:bg-[#1E1E1E]/50 transition-colors">
                      <td className="py-3 px-4 text-sm font-mono text-[#F5F1E8]">{sp.name}</td>
                      <td className="py-3 px-4 text-sm text-[rgba(245,241,232,0.72)]">{sp.purpose}</td>
                      <td className="py-3 px-4 text-xs font-mono text-[rgba(245,241,232,0.48)]">{sp.location}</td>
                      <td className="py-3 px-4">
                        {sp.dpa && <CheckCircle2 className="w-4 h-4 text-[#22C55E]" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Encryption & Data Residency */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionHeader number="05" title="Encryption & Infrastructure" />
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {[
                { title: 'In Transit', items: ['TLS 1.3 for all connections', 'Certificate pinning for API calls', 'HSTS enforced'], icon: Lock },
                { title: 'At Rest', items: ['AES-256 encryption for all data', 'Encrypted database backups', 'Key rotation every 90 days'], icon: Server },
                { title: 'Access Control', items: ['Role-based access (RBAC)', 'SSO/SAML for Enterprise', 'Audit logging on all actions'], icon: Eye },
              ].map((card) => (
                <div key={card.title} className="bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] p-5">
                  <card.icon className="w-5 h-5 text-[#FF4D00] mb-3" />
                  <h4 className="text-sm font-mono text-[#F5F1E8] mb-3 tracking-wider uppercase">{card.title}</h4>
                  <ul className="space-y-2">
                    {card.items.map(item => (
                      <li key={item} className="flex items-start gap-2 text-xs text-[rgba(245,241,232,0.72)]">
                        <CheckCircle2 className="w-3 h-3 text-[#22C55E] shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Governance Sandbox */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionHeader number="06" title="See How Audit Trails Work" />
            <p className="text-sm text-[rgba(245,241,232,0.72)] mt-4 mb-8 max-w-2xl font-body">
              Every Fray debate produces a complete audit trail: who decided what, which models contributed,
              where they disagreed, and how consensus was reached. Try it yourself.
            </p>
            <DebateSandbox governance={true} />
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp}>
            <SectionHeader number="07" title="Security FAQ" />
            <div className="mt-8 space-y-2">
              {FAQ_ITEMS.map((item, i) => (
                <FAQItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-display-md font-display text-[#F5F1E8] uppercase mb-4">
              Ready to evaluate Fray<br />for your organization?
            </h2>
            <p className="text-sm text-[rgba(245,241,232,0.72)] mb-8 font-body">
              Our security team is available to answer technical questions and provide additional documentation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://calendly.com/fray-ai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors"
              >
                Talk to Our Team
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="/enterprise"
                className="flex items-center gap-2 px-6 py-3 border border-[rgba(245,241,232,0.08)] text-[#F5F1E8] font-display text-sm font-bold uppercase tracking-wider hover:border-[#FF4D00] transition-colors"
              >
                View Enterprise Features
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function SectionHeader({ number, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-mono text-[#FF4D00]">{number}</span>
      <div className="w-8 h-px bg-[#FF4D00]/40" />
      <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase">{title}</h2>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    active: { color: '#22C55E', label: 'Active' },
    'in-progress': { color: '#F2A900', label: 'In Progress' },
    planned: { color: '#00D4FF', label: 'Planned' },
  };
  const c = config[status] || config.planned;
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono tracking-wider uppercase" style={{ borderColor: c.color + '40', color: c.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.color }} />
      {c.label}
    </span>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[rgba(245,241,232,0.08)] bg-[#1E1E1E]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <span className="text-sm font-mono text-[#F5F1E8] pr-4">{question}</span>
        <ChevronDown className={`w-4 h-4 text-[rgba(245,241,232,0.48)] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-[rgba(245,241,232,0.04)]">
          <p className="text-sm text-[rgba(245,241,232,0.72)] leading-relaxed pt-3 font-body">{answer}</p>
        </div>
      )}
    </div>
  );
}
